/** Crear presupuesto SA desde FOTO (Fase 7 — primera entrega).
 *  Sube/toma foto → IA extrae → "Esto entendí de tu solicitud" (confirmado/interpretado/ilegible)
 *  → usuario corrige solo lo ambiguo → crea borrador y envía la geometría al Modulador (flujo normal). */
import React, { useRef, useState } from 'react';
import api from '@/api/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Camera, Spinner, CheckCircle, Warning, Question, ArrowRight, FileArrowUp } from '@phosphor-icons/react';
import { derivarDiseno } from '@/components/sa/Modulador';
import { detectarUniones } from '@/components/sa/simbolos/modeloTecnico';
import { HerrajeCatalogo } from '@/components/sa/HerrajeCatalogo';

const Chip = ({ estado }) => {
  const map = {
    confirmado: { c: 'bg-emerald-500/15 text-emerald-500', t: 'Confirmado', I: CheckCircle },
    interpretado: { c: 'bg-amber-500/15 text-amber-500', t: 'Interpretado', I: Warning },
    ilegible: { c: 'bg-red-500/15 text-red-400', t: 'Ilegible — pendiente', I: Question },
    dudoso: { c: 'bg-orange-500/20 text-orange-500', t: 'Lectura dudosa', I: Warning },
  };
  const m = map[estado] || map.interpretado;
  return <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-black uppercase ${m.c}`} data-testid={`foto-chip-${estado}`}><m.I size={10} weight="bold" />{m.t}</span>;
};

const Campo = ({ label, dato, onChange, testid, placeholder }) => (
  <div>
    <label className="text-[10px] text-[var(--text-muted)] flex items-center gap-1.5">{label} {dato && <Chip estado={dato.estado} />}</label>
    <Input className="h-9 text-xs" value={dato?.valor ?? ''} placeholder={placeholder || (dato?.estado === 'ilegible' ? 'No se pudo leer — complétalo' : '')}
      onChange={(e) => onChange({ ...dato, valor: e.target.value, estado: 'confirmado' })} data-testid={testid} />
    {dato?.lecturas && (dato.valor == null || dato.valor === '') && (
      <div className="flex gap-1 flex-wrap mt-0.5">
        {dato.lecturas.map((l, k) => l != null && (
          <button key={k} type="button" className="text-[9px] px-1.5 py-0.5 rounded border border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
            onClick={() => onChange({ ...dato, valor: l, estado: 'confirmado' })} data-testid={`${testid}-lectura-${k}`}>
            Usar lectura {k ? 'B' : 'A'}: {l}
          </button>
        ))}
      </div>
    )}
    {dato?.texto_original && <p className="text-[9px] text-[var(--text-muted)]">Leído: "{dato.texto_original}"</p>}
  </div>
);

const redimensionar = (file, max = 1600) => new Promise((res, rej) => {
  const img = new Image();
  img.onload = () => {
    const esc = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * esc); c.height = Math.round(img.height * esc);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    res(c.toDataURL('image/jpeg', 0.85));
  };
  img.onerror = rej;
  img.src = URL.createObjectURL(file);
});

export const CrearDesdeFoto = ({ open, onClose, onCreated }) => {
  const [foto, setFoto] = useState(null);
  const [mime, setMime] = useState('image/jpeg');
  const [analizando, setAnalizando] = useState(false);
  const [d, setD] = useState(null);
  const [creando, setCreando] = useState(false);
  const [pickCod, setPickCod] = useState(null);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const elegir = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type === 'application/pdf' || /\.pdf$/i.test(f.name)) {
      if (f.size > 8 * 1024 * 1024) return toast.error('PDF máximo 8MB');
      const fr = new FileReader();
      fr.onload = () => { setFoto(fr.result); setMime('application/pdf'); setD(null); };
      return fr.readAsDataURL(f);
    }
    if (!/image\/(jpeg|png|webp)/.test(f.type) && !/\.(jpe?g|png|webp)$/i.test(f.name)) return toast.error('Usa una foto JPG/PNG o un plano PDF');
    try { setFoto(await redimensionar(f)); setMime('image/jpeg'); setD(null); } catch { toast.error('No se pudo leer la imagen'); }
  };

  const analizar = async () => {
    setAnalizando(true);
    try {
      const { data } = await api.post('/sa/foto/analizar', { imagen_base64: foto, mime }, { timeout: 200000 });
      if (data.datos?.es_solicitud === false) {
        toast.error(`El archivo no parece una solicitud/plano: ${data.datos.motivo || 'revisa el archivo'}`, { duration: 8000 });
        setD(null);
      } else setD({
        ...data.datos,
        codigos_detectados: (data.datos.codigos_detectados || []).map((c) => ({ ...c, aplicar: !!c.vinculo?.configurado && c.estado !== 'dudoso' })),
        herrajes: (data.datos.herrajes || []).map((h) => ({ ...h, aplicar: false })),
      });
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo analizar el archivo'); }
    setAnalizando(false);
  };

  const setCampo = (k, v) => setD({ ...d, [k]: v });
  const setVidrio = (k, v) => setD({ ...d, vidrio: { ...(d.vidrio || {}), [k]: v } });
  const setElem = (i, patch) => { const a = [...(d.elementos || [])]; a[i] = { ...a[i], ...patch }; setD({ ...d, elementos: a }); };

  const pendientes = () => {
    const p = [];
    ['cliente', 'direccion', 'telefono'].forEach((k) => { if (d?.[k]?.estado === 'ilegible') p.push(k); });
    (d?.elementos || []).forEach((el, i) => {
      if (el.alto_mm?.estado === 'ilegible' || !el.alto_mm?.valor) p.push(`alto del elemento ${i + 1}`);
      (el.modulos || []).forEach((m, j) => { if (m.ancho_mm?.estado === 'ilegible' || !m.ancho_mm?.valor) p.push(`ancho módulo ${j + 1} del elemento ${i + 1}`); });
    });
    return p;
  };

  const crearBorrador = async () => {
    setCreando(true);
    try {
      const herrTxt = (d.herrajes || []).map((h) => {
        const cat = h.catalogo?.estado === 'claro' ? ` → ${h.catalogo.coincidencias[0].codigo}` : h.catalogo?.estado === 'ambiguo' ? ' → VARIAS COINCIDENCIAS (elegir en catálogo)' : ' → SIN COINCIDENCIA en catálogo';
        return `• ${h.texto}${h.cantidad?.valor ? ` ×${h.cantidad.valor}` : ' (cantidad pendiente)'}${cat}`;
      }).join('\n');
      const obsTxt = (d.observaciones || []).map((o) => `• ${o.texto}`).join('\n');
      const pend = pendientes();
      const notas = ['[CREADO DESDE FOTO — revisar en Modulador antes de calcular]',
        herrTxt && `HERRAJES LEÍDOS EN LA SOLICITUD (validar con reglas técnicas, NO agregados al despiece):\n${herrTxt}`,
        obsTxt && `OBSERVACIONES:\n${obsTxt}`,
        pend.length ? `⚠️ DATOS PENDIENTES/ILEGIBLES: ${pend.join(', ')}` : ''].filter(Boolean).join('\n\n');
      const { data: pj } = await api.post('/sa/proyectos', {
        nombre: `Solicitud ${d.cliente?.valor || 'desde foto'}${d.direccion?.valor ? ` — ${d.direccion.valor}` : ''}`.slice(0, 80),
        cliente_nombre: d.cliente?.valor || '', cliente_telefono: d.telefono?.valor || '',
        direccion: d.direccion?.valor || '', notas });
      let idx = 0;
      for (const el of d.elementos || []) {
        const fam = ['pabm', 'vtf'].includes(el.familia_sugerida) ? el.familia_sugerida : (el.modulos || []).some((m) => m.tipo === 'puerta') ? 'pabm' : 'vtf';
        const alto = Number(el.alto_mm?.valor) || 0;
        const puerta = (el.modulos || []).find((m) => m.tipo === 'puerta');
        const fijos = (el.modulos || []).filter((m) => m.tipo === 'fijo').map((m) => ({
          posicion: m.posicion === 'izquierda' ? 'izquierda' : 'derecha',
          vano_ancho: Number(m.ancho_mm?.valor) || 0, vano_alto: alto, barra: false }));
        const receta = {
          familia: fam, vano_alto: alto,
          vano_ancho: fam === 'pabm' ? (Number(puerta?.ancho_mm?.valor) || 0) : (Number(el.ancho_total_mm?.valor) || Number(el.modulos?.[0]?.ancho_mm?.valor) || 0),
          apertura: el.apertura?.valor || 'derecha',
          embisagrado: el.embisagrado?.valor === 'fijo' ? 'fijo_180' : 'muro',
          vidrio: { tipo: d.vidrio?.tipo?.valor || '', espesor: d.vidrio?.espesor?.valor || '', color: d.vidrio?.color?.valor || '' },
          fijos: fam === 'pabm' ? fijos : [], despiece_estado: 'pendiente' };
        const dis = derivarDiseno(receta, fam);
        // creación NUEVA → la geometría propone las uniones (el usuario las edita en el Modulador)
        const unionesDet = detectarUniones(receta, fam);
        if (unionesDet.length) receta.uniones = unionesDet;
        // aplicar SOLO lo validado por el usuario, en SU diseño correspondiente (una línea al calcular)
        {
          const centro = (rol) => { let x = 90; for (const m of dis.modulos) { if (m.rol === rol) return { x: Math.round(x + m.ancho_mm / 2), y: Math.round(90 + dis.alto_mm / 2) }; x += m.ancho_mm; } return { x: Math.round(90 + dis.ancho_mm / 2), y: Math.round(90 + dis.alto_mm / 2) }; };
          const borde = (rolIzq) => { let x = 90; for (const m of dis.modulos) { x += m.ancho_mm; if (m.rol === rolIzq) return { x: Math.round(x), y: Math.round(90 + dis.alto_mm / 2) }; } return centro('puerta'); };
          const rolPor = (mod) => mod === 'izquierda' ? (dis.modulos.find((m) => String(m.rol).includes('izquierda'))?.rol || '')
            : mod === 'derecha' ? ([...dis.modulos].reverse().find((m) => String(m.rol).startsWith('fijo'))?.rol || '')
            : (fam === 'pabm' ? 'puerta' : (dis.modulos[0]?.rol || ''));
          const unionPara = (c) => {
            const us = (receta.uniones || []).filter((u) => u.componente_b !== 'muro' && u.componente_a !== 'muro');
            if (!c.sobre_division || !us.length) return null;
            return (c.entre === 'centro-derecha'
              ? us.find((u) => /der/.test(u.componente_a + u.componente_b))
              : us.find((u) => /izquierda/.test(u.componente_a + u.componente_b))) || us[0];
          };
          const hv = [];
          (d.codigos_detectados || []).forEach((c, k) => {
            if (!c.aplicar || !c.vinculo?.configurado) return;
            if ((Number(c.diseno) || 1) !== idx + 1) return;
            const p = c.vinculo.producto;
            const un = unionPara(c);
            const rol = un ? '' : rolPor(c.modulo);
            const pos = un ? (() => {
              const orden = dis.modulos.map((m) => m.rol);
              const izq = orden.indexOf(un.componente_a) <= orden.indexOf(un.componente_b) ? un.componente_a : un.componente_b;
              return borde(izq);
            })() : centro(rol);
            hv.push({ hid: `h_foto_${k}`, sku: p.codigo, nombre: p.nombre, unidad: p.unidad || 'unidad',
              cantidad: Number(c.cantidad) || 1, acabado: p.acabado || '', costo: Number(p.costo || 0),
              observaciones: `Detectado en plano (${c.codigo}${c.posicion_texto ? ` — ${c.posicion_texto}` : ''})`,
              modulo_id: rol, union_id: un ? `${un.componente_a}↔${un.componente_b}` : '',
              x: pos.x, y: pos.y, pos_rel: un ? 'union' : rol ? 'modulo' : 'libre',
              orientacion: 'vertical', etiqueta: `H${hv.length + 1}`, codigo_corto: c.codigo });
          });
          (d.herrajes || []).forEach((h, k) => {
            if (idx !== 0) return;
            if (!h.aplicar || h.catalogo?.estado !== 'claro') return;
            const p = h.catalogo.coincidencias[0]; const pos = centro(fam === 'pabm' ? 'puerta' : dis.modulos[0]?.rol);
            hv.push({ hid: `h_fotoh_${k}`, sku: p.codigo, nombre: p.nombre, unidad: p.unidad || 'unidad',
              cantidad: Number(h.cantidad?.valor) || 1, acabado: p.acabado || '', costo: 0,
              observaciones: `Leído en solicitud: ${h.texto}`, modulo_id: '', union_id: '',
              x: pos.x, y: Math.min(pos.y + 250 + k * 120, 90 + dis.alto_mm - 60), pos_rel: 'libre', orientacion: 'vertical', etiqueta: `H${hv.length + 1}` });
          });
          if (hv.length) receta.herrajes_visuales = hv;
        }
        await api.post(`/sa/proyectos/${pj.id}/elementos`, {
          familia: fam, nombre: (el.descripcion || (fam === 'pabm' ? 'Puerta + fijos (desde foto)' : 'Vidrio fijo (desde foto)')).slice(0, 60),
          cantidad: 1, receta, diseno: dis, despiece: [],
          descripcion: el.notas_dibujo || '' });
        idx += 1;
      }
      toast.success('Borrador creado — revisa la geometría en el Modulador y calcula la receta', { duration: 7000 });
      onCreated?.(pj);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo crear el borrador'); }
    setCreando(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[640px] max-h-[92vh] overflow-y-auto" data-testid="foto-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Camera size={20} className="text-[#0EA5E9]" /> Crear presupuesto desde foto</DialogTitle>
          <DialogDescription className="text-xs">Sube o toma una foto del formato de solicitud. La IA solo EXTRAE la información — las reglas técnicas, cantidades y costos los pone el sistema. Nada se calcula sin tu revisión.</DialogDescription>
        </DialogHeader>

        {!d && (
          <div className="space-y-3">
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={elegir} data-testid="foto-input" />
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={elegir} data-testid="foto-input-camara" />
            {!foto ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button type="button" onClick={() => fileRef.current?.click()} data-testid="foto-subir"
                  className="border-2 border-dashed border-[var(--border)] rounded-xl p-6 flex flex-col items-center gap-2 text-[var(--text-muted)] hover:border-[#0EA5E9] hover:text-[#0EA5E9]">
                  <FileArrowUp size={36} /> <span className="text-sm font-bold">Subir archivo</span>
                  <span className="text-[10px]">JPG · PNG · PDF desde tu galería o archivos</span>
                </button>
                <button type="button" onClick={() => camRef.current?.click()} data-testid="foto-tomar"
                  className="border-2 border-dashed border-[var(--border)] rounded-xl p-6 flex flex-col items-center gap-2 text-[var(--text-muted)] hover:border-[#0EA5E9] hover:text-[#0EA5E9]">
                  <Camera size={36} /> <span className="text-sm font-bold">Tomar foto</span>
                  <span className="text-[10px]">Usa la cámara del dispositivo</span>
                </button>
              </div>
            ) : (
              <>
                {mime === 'application/pdf'
                  ? <div className="w-full rounded-lg border border-[var(--border)] p-6 text-center text-xs text-[var(--text-muted)]" data-testid="foto-preview">📄 Plano PDF listo — se analizará la página 1</div>
                  : <img src={foto} alt="solicitud" className="w-full max-h-[320px] object-contain rounded-lg border border-[var(--border)]" data-testid="foto-preview" />}
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => fileRef.current?.click()} data-testid="foto-cambiar">Cambiar foto</Button>
                  <Button className="flex-1 bg-[#0EA5E9] hover:bg-[#0284C7] text-white" onClick={analizar} disabled={analizando} data-testid="foto-analizar">
                    {analizando ? <><Spinner size={16} className="animate-spin mr-1" /> Analizando…</> : <>Analizar con IA <ArrowRight size={14} className="ml-1" /></>}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {d && (
          <div className="space-y-3" data-testid="foto-entendi">
            <p className="text-xs font-black uppercase text-[#0EA5E9]">Esto entendí de tu solicitud — corrige solo lo marcado</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Campo label="Cliente" dato={d.cliente} onChange={(v) => setCampo('cliente', v)} testid="foto-campo-cliente" />
              <Campo label="Teléfono" dato={d.telefono} onChange={(v) => setCampo('telefono', v)} testid="foto-campo-telefono" />
              <Campo label="Dirección" dato={d.direccion} onChange={(v) => setCampo('direccion', v)} testid="foto-campo-direccion" />
              <Campo label="Asesor/Vendedor" dato={d.asesor} onChange={(v) => setCampo('asesor', v)} testid="foto-campo-asesor" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Campo label="Vidrio" dato={d.vidrio?.tipo} onChange={(v) => setVidrio('tipo', v)} testid="foto-campo-vidrio-tipo" />
              <Campo label="Espesor" dato={d.vidrio?.espesor} onChange={(v) => setVidrio('espesor', v)} testid="foto-campo-vidrio-espesor" />
              <Campo label="Color" dato={d.vidrio?.color} onChange={(v) => setVidrio('color', v)} testid="foto-campo-vidrio-color" />
            </div>

            {(d.elementos || []).map((el, i) => (
              <div key={i} className="border border-[var(--border)] rounded-lg p-2 space-y-1.5" data-testid={`foto-elemento-${i}`}>
                <p className="text-[10px] font-black uppercase text-[var(--text-muted)]">
                  {el.etiqueta || `Elemento ${i + 1}`} — {el.descripcion || el.familia_sugerida}
                  {el.pareo === 'pendiente' && <span className="ml-1.5 text-orange-500">⚠ correspondencia entre lecturas pendiente</span>}
                  {el.notas_dibujo && <span className="normal-case font-normal"> · {el.notas_dibujo}</span>}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                  <Campo label="Alto (mm)" dato={el.alto_mm} onChange={(v) => setElem(i, { alto_mm: v })} testid={`foto-el-${i}-alto`} />
                  {(el.modulos || []).map((m, j) => (
                    <Campo key={j} label={`${m.tipo === 'puerta' ? 'Puerta' : `Fijo ${m.posicion || ''}`} ancho (mm)`} dato={m.ancho_mm}
                      onChange={(v) => { const mods = [...el.modulos]; mods[j] = { ...m, ancho_mm: v }; setElem(i, { modulos: mods }); }}
                      testid={`foto-el-${i}-mod-${j}`} />
                  ))}
                </div>
                {el.apertura && (
                  <p className="text-[10px] text-[var(--text-muted)]">
                    Apertura: <b>{el.apertura.valor || '—'}</b> <Chip estado={el.apertura.estado} />
                    {el.apertura.estado !== 'confirmado' && ['izquierda', 'derecha'].map((op) => (
                      <button key={op} type="button" className="ml-1 text-[9px] px-1.5 py-0.5 rounded border border-[#0EA5E9]/50 text-[#0EA5E9] hover:bg-[#0EA5E9]/10"
                        onClick={() => setElem(i, { apertura: { valor: op, estado: 'confirmado' } })} data-testid={`foto-el-${i}-apertura-${op}`}>
                        {op === 'izquierda' ? 'Izquierda' : 'Derecha'}
                      </button>
                    ))}
                    {' '}· Embisagrado leído: <b>{el.embisagrado?.valor || '—'}</b>
                  </p>
                )}
              </div>
            ))}

            {(d.discrepancias || []).length > 0 && (
              <div className="border border-orange-500/50 bg-orange-500/5 rounded-lg p-2" data-testid="foto-discrepancias">
                <p className="text-[10px] font-black uppercase text-orange-500">⚠ Diferencias entre dibujo y lista (no se igualan solas)</p>
                {d.discrepancias.map((q, i) => <p key={i} className="text-[11px] text-[var(--text)]">• {q}</p>)}
              </div>
            )}

            {(d.preguntas || []).length > 0 && (
              <div className="border border-amber-500/40 bg-amber-500/5 rounded-lg p-2" data-testid="foto-preguntas">
                <p className="text-[10px] font-black uppercase text-amber-600">Necesito confirmar (responde editando los campos)</p>
                {d.preguntas.map((q, i) => <p key={i} className="text-[11px] text-[var(--text)]">• {q}</p>)}
              </div>
            )}

            {(d.codigos_detectados || []).length > 0 && (
              <div className="border border-[var(--border)] rounded-lg p-2 space-y-1.5" data-testid="foto-codigos">
                <p className="text-[10px] font-black uppercase text-[var(--text-muted)]">Códigos cortos detectados en el plano</p>
                {d.codigos_detectados.map((c, i) => (
                  <div key={i} className="text-[11px] text-[var(--text)]" data-testid={`foto-codigo-${i}`}>
                    <label className="flex items-start gap-1.5">
                      <input type="checkbox" className="mt-0.5" checked={!!c.aplicar} disabled={!c.vinculo?.configurado || c.estado === 'dudoso'}
                        onChange={(e) => { const a = [...d.codigos_detectados]; a[i] = { ...c, aplicar: e.target.checked }; setD({ ...d, codigos_detectados: a }); }}
                        data-testid={`foto-codigo-aplicar-${i}`} />
                      <span>
                        Detecté <b className="text-[#0EA5E9]">{c.codigo}</b>{c.posicion_texto ? ` en ${c.posicion_texto}` : ''}{c.cantidad ? ` ×${c.cantidad}` : ''} <Chip estado={c.estado} />{c.nota_lectura ? <em className="text-[9px] text-[var(--text-muted)]"> ({c.nota_lectura})</em> : ''}{' '}
                        {c.estado === 'dudoso' && c.opciones ? (
                          <span className="text-orange-500 font-bold">
                            {c.opciones.map((op, k) => (
                              <button key={k} type="button" className="underline mx-1" data-testid={`foto-codigo-opcion-${i}-${k}`}
                                onClick={() => { const a = [...d.codigos_detectados];
                                  a[i] = { ...c, codigo: op, estado: 'confirmado', opciones: null,
                                    vinculo: c.vinculo_opciones?.[k] || { configurado: false },
                                    aplicar: !!c.vinculo_opciones?.[k]?.configurado };
                                  setD({ ...d, codigos_detectados: a }); }}>
                                Lectura {k ? 'B' : 'A'}: {op}{c.vinculo_opciones?.[k]?.producto ? ` — ${c.vinculo_opciones[k].producto.nombre}` : ''}
                              </button>
                            ))} ¿Cuál corresponde?
                          </span>
                        ) : c.vinculo?.configurado
                          ? <span className="text-emerald-500 font-bold">→ {c.vinculo.producto.nombre} · SKU {c.vinculo.producto.codigo}{Number(c.vinculo.producto.costo || 0) ? '' : ' · ⚠ costo pendiente'}</span>
                          : <span className="text-red-400 font-bold">⚠ Código {c.codigo} no configurado{' '}
                              <button type="button" className="underline" onClick={() => setPickCod(i)} data-testid={`foto-codigo-vincular-${i}`}>elegir producto Soluarq</button>
                            </span>}
                      </span>
                    </label>
                  </div>
                ))}
                <p className="text-[9px] text-[var(--text-muted)]">Marcados con ✓ se aplican al borrador como herrajes del módulo (una sola línea de despiece al calcular).</p>
              </div>
            )}

            {(d.herrajes || []).length > 0 && (
              <div className="border border-[var(--border)] rounded-lg p-2 space-y-1" data-testid="foto-herrajes">
                <p className="text-[10px] font-black uppercase text-[var(--text-muted)]">Herrajes leídos (se validan con el Catálogo Soluarq — NO se agregan solos al despiece)</p>
                {d.herrajes.map((h, i) => (
                  <label key={i} className="text-[11px] text-[var(--text)] flex items-start gap-1.5">
                    <input type="checkbox" className="mt-0.5" checked={!!h.aplicar} disabled={h.catalogo?.estado !== 'claro'}
                      onChange={(e) => { const a = [...d.herrajes]; a[i] = { ...h, aplicar: e.target.checked }; setD({ ...d, herrajes: a }); }}
                      data-testid={`foto-herraje-aplicar-${i}`} />
                    <span>
                    • {h.texto} {h.cantidad?.valor ? `×${h.cantidad.valor}` : '(cantidad pendiente)'} <Chip estado={h.estado} />{' '}
                    {h.catalogo?.estado === 'claro' && <span className="text-emerald-500 font-bold">→ {h.catalogo.coincidencias[0].codigo} {h.catalogo.coincidencias[0].nombre}</span>}
                    {h.catalogo?.estado === 'ambiguo' && <span className="text-amber-500 font-bold">→ {h.catalogo.coincidencias.length} coincidencias — elegir en el Modulador</span>}
                    {h.catalogo?.estado === 'sin_coincidencia' && <span className="text-red-400 font-bold">→ sin coincidencia en catálogo — seleccionar manualmente</span>}
                    </span>
                  </label>
                ))}
              </div>
            )}
            {(d.observaciones || []).length > 0 && (
              <div className="text-[11px] text-[var(--text-muted)]" data-testid="foto-observaciones">
                <b>Observaciones:</b> {d.observaciones.map((o) => o.texto).join(' · ')}
              </div>
            )}
            {pendientes().length > 0 && (
              <p className="text-[11px] font-bold text-red-400" data-testid="foto-pendientes">⚠️ Pendientes de confirmar: {pendientes().join(', ')}. Puedes completarlos aquí o después en el Modulador.</p>
            )}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setD(null); }} data-testid="foto-volver">Volver a la foto</Button>
              <Button className="flex-1 bg-[#0EA5E9] hover:bg-[#0284C7] text-white" onClick={crearBorrador} disabled={creando || !(d.elementos || []).length} data-testid="foto-crear-borrador">
                {creando ? <><Spinner size={16} className="animate-spin mr-1" /> Creando…</> : 'Crear borrador y revisar en Modulador'}
              </Button>
            </div>
          </div>
        )}
        {pickCod != null && d && (
          <HerrajeCatalogo open onClose={() => setPickCod(null)}
            anclaLabel={`código ${d.codigos_detectados[pickCod]?.codigo}`}
            onSelect={async (item) => {
              if (!item.codigo) return toast.error('Elige un producto del catálogo (con SKU)');
              const c = d.codigos_detectados[pickCod];
              try {
                await api.post('/sa/codigos', { codigo: c.codigo, item_codigo: item.codigo, observaciones: 'Creado desde análisis de plano' });
                toast.success(`Código ${c.codigo} → ${item.nombre} guardado para futuras cotizaciones`);
              } catch (e) { toast.error(e.response?.data?.detail || 'Sin permiso para guardar la relación — se usará solo en este borrador'); }
              const a = [...d.codigos_detectados];
              a[pickCod] = { ...c, aplicar: true, vinculo: { configurado: true, producto: { codigo: item.codigo, nombre: item.nombre, acabado: item.acabado || '', unidad: item.unidad || 'unidad', costo: Number(item.costo || 0) } } };
              setD({ ...d, codigos_detectados: a });
              setPickCod(null);
            }} />
        )}
      </DialogContent>
    </Dialog>
  );
};
